import React from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import styles from './channel-programming-interface.module.scss';
import { Tooltip, Modal, Checkbox, Select, Row, Col, Spin } from 'antd';

/**
 * CHANNEL PROGRAMMING INTERFACE - IMPORTANT FOR FUTURE DEVELOPMENT:
 * 
 * This is the detailed programming interface for individual channels.
 * For the overview page (showing all channels), see: channel-programming-overview.tsx
 * 
 * This site uses a HYBRID approach for UI components:
 * - BOOTSTRAP for layout: Row, Col, Container, Card, Button, Form (as BootstrapForm)
 * - ANT DESIGN only for specific components: Select, Modal, Tooltip, Checkbox, Icons
 * 
 * CRITICAL: Never mix Bootstrap Row/Col with Ant Design Row/Col!
 * - Use Bootstrap: <Col md={4}> (Bootstrap syntax)
 * - NOT Ant Design: <Col span={8}> (Ant Design syntax)
 * 
 * For filters, use Bootstrap Form + FormItem + Ant Design Select components.
 * Reference: music-vendor-roles.module.scss for proper filter styling.
 */
import PageTitle from '@/common/PageTitle/PageTitle';
import {
  InfoCircleOutlined,
  PlusOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  CaretDownOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import kkFavicon from '@/assets/images/kk-favicon.png';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import WaveSurfer from '@wavesurfer/react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChannelArtwork, DEFAULT_ALBUM_ART } from '@/utils/artwork';
import { useListTracksQuery } from '@/features/tracks/tracksApi';
import { useListArtistsQuery } from '@/features/artists/artistsApi';
import { getMusicApprovedCdnBaseUrl } from '@/lib/adminApi';
import { formatEntityRef } from '@/lib/entityRef';
import type { Rotation, RotationTrack, ScheduleView } from '@/features/channels/types';
import {
  useListChannelsQuery,
  useGetChannelProgrammingQuery,
  useCreateChannelRotationMutation,
  useDeleteChannelRotationMutation,
  useAddChannelRotationTrackMutation,
  useRemoveChannelRotationTrackMutation,
  useMoveChannelRotationTrackMutation,
  useUpdateChannelRotationMutation,
  useUpdateChannelScheduleMutation,
  useCancelChannelScheduleMutation,
  useGoLiveChannelScheduleMutation,
  extractChannelErrorMessage,
} from '@/features/channels/channelsApi';
import {
  toUiStatus,
  formatChannelUpdatedAt,
} from '@/features/channels/channelStatus';
import type { PreviewRotation } from '@/features/channels/channelPreviewPlayback';
import { useChannelPreviewPlayback } from '@/features/channels/useChannelPreviewPlayback';
import toast from 'react-hot-toast';

// Copy SongRecord type from songs.tsx
// This should ideally be shared in a types file, but for now, copy here:
type SongRecord = {
  key: string;
  name: string;
  album: string;
  recordLabel: string;
  isrc: string;
  duration: string;
  kkExclusive: string;
  processed: string;
  streamUrl?: string;
  /** Canonical catalog songType for library filters. */
  songType?: 'kk-original' | 'commercial' | string;
};

type LibraryKindFilter = 'all' | 'commercial' | 'original';

const LIBRARY_FILTER_BTN_BASE: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 10px',
  borderRadius: 4,
  border: '1px solid #bbb',
  fontWeight: 500,
  cursor: 'pointer',
  marginRight: 2,
};

function libraryFilterBtnStyle(active: boolean): React.CSSProperties {
  return {
    ...LIBRARY_FILTER_BTN_BASE,
    background: active ? '#e21e26' : '#fff',
    color: active ? '#fff' : '#23272b',
    border: active ? '1px solid #e21e26' : '1px solid #bbb',
    fontWeight: active ? 600 : 500,
  };
}

// Channel data will be loaded dynamically from the channelId parameter


/** API `rotationSet` keys (max 8 chars). UI labels are separate — see rotationSetLabel. */
const DEFAULT_ROTATION_SET_KEYS = ['A', 'B', 'C'];
const MAX_ROTATION_SET_LEN = 8;
/** Human-facing rotation name (`categoryLabel`) — matches catalog API. */
const MAX_CATEGORY_LABEL_LEN = 256;

/** Display label for a rotation-set key (`A` → `Rotation A`). */
function rotationSetLabel(key: string): string {
  if (/^[A-Za-z]$/.test(key)) return `Rotation ${key.toUpperCase()}`;
  return key;
}

/**
 * Normalize operator input into an API rotationSet key.
 * Accepts `A`, `Fest`, or `Rotation A` → `A`. Returns null if empty or >8 chars.
 */
function parseRotationSetKey(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const asRotation = /^Rotation\s+(.+)$/i.exec(trimmed);
  const key = (asRotation ? asRotation[1] : trimmed).trim();
  if (!key || key.length > MAX_ROTATION_SET_LEN) return null;
  return key;
}

/** Operator-facing slot label (left column), e.g. Rotation A01. */
function rotationSlotLabel(rotation: UiRotation, indexInSchedule: number): string {
  if (rotation.labelName?.trim()) return rotation.labelName.trim();
  return `Rotation ${String(indexInSchedule + 1).padStart(2, '0')}`;
}

type DuplicateSongNotice = {
  headline: string;
  locationLabel: string | null;
  locationRotationId: string | null;
};

function buildSongAlreadyInScheduleNotice(
  songName: string,
  rotation: UiRotation,
  indexInSchedule: number,
): DuplicateSongNotice {
  return {
    headline: `${songName} is already in use in this Rotation Schedule`,
    locationLabel: rotationSlotLabel(rotation, indexInSchedule),
    locationRotationId: rotation.id,
  };
}

type UiRotation = {
  id: string;
  name: string;
  labelName: string;
  songs: number;
  tracks: RotationTrack[];
  order: number;
};

type ModalSong = {
  rotationTrackId: string;
  trackId: string;
  label: string;
};

function trackDisplayLabel(t: RotationTrack): string {
  if (t.artistName && t.artistName.trim()) {
    return `${t.artistName} - ${t.trackTitle}`;
  }
  return t.trackTitle || t.trackId;
}

function rotationsFromSchedule(view: ScheduleView | null | undefined): UiRotation[] {
  if (!view?.rotations) return [];
  return [...view.rotations]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((r: Rotation) => ({
      id: r.rotationId,
      name: r.categoryLabel || r.name,
      labelName: r.name,
      songs: r.trackCount ?? (r.tracks?.length ?? 0),
      tracks: r.tracks || [],
      order: r.order ?? 0,
    }));
}

function groupStagingBySet(view: ScheduleView | null | undefined, extraSets: string[]): Record<string, UiRotation[]> {
  const map: Record<string, UiRotation[]> = {};
  for (const set of DEFAULT_ROTATION_SET_KEYS) map[set] = [];
  for (const set of extraSets) {
    if (!map[set]) map[set] = [];
  }
  for (const r of view?.rotations || []) {
    // Prefer short API keys; coerce legacy "Rotation A" labels if ever stored.
    const set = parseRotationSetKey(r.rotationSet || 'A') || 'A';
    if (!map[set]) map[set] = [];
    map[set].push({
      id: r.rotationId,
      name: r.categoryLabel || r.name,
      labelName: r.name,
      songs: r.trackCount ?? (r.tracks?.length ?? 0),
      tracks: r.tracks || [],
      order: r.order ?? 0,
    });
  }
  for (const key of Object.keys(map)) {
    map[key] = [...map[key]].sort((a, b) => a.order - b.order);
  }
  return map;
}

function songsByRotation(rotations: UiRotation[]): Record<string, ModalSong[]> {
  const out: Record<string, ModalSong[]> = {};
  for (const r of rotations) {
    out[r.id] = (r.tracks || []).map((t) => ({
      rotationTrackId: t.rotationTrackId,
      trackId: t.trackId,
      label: trackDisplayLabel(t),
    }));
  }
  return out;
}

function toPreviewRotations(rotations: UiRotation[]): PreviewRotation[] {
  return rotations.map((r, idx) => ({
    id: r.id,
    slotLabel: rotationSlotLabel(r, idx),
    categoryLabel: r.name,
    tracks: (r.tracks || []).map((t) => ({
      trackId: t.trackId,
      label: trackDisplayLabel(t),
    })),
  }));
}

function SortableRotation({ id, children }: { id: string; children: (isDragging: boolean) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    opacity: isDragging ? 0.7 : 1,
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children(isDragging)}
    </div>
  );
}

function DraggableSong({ id, children }: { id: string; children: (handleProps: any) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 2 : 1,
    cursor: 'grab',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
}

function DroppableRotation({ id, isDragging, children }: { id: string; isDragging?: boolean; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const style = {
    background: isDragging ? '#e0e0e0' : (isOver ? '#f0f0f0' : '#f7f7f7'),
    borderRadius: 7,
    padding: '10px 14px',
    marginBottom: 10,
    fontSize: 13,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    cursor: 'pointer',
    minHeight: 48,
    border: isDragging ? '2px solid #23272b' : (isOver ? '2px dashed #bbb' : 'none'),
    transition: 'background 0.15s, border 0.15s',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

const ChannelProgramming: React.FC = () => {
  // SCSS module now in use - migrating from inline styles while preserving exact UI
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();

  const { data: channelsData, isLoading: isLoadingChannels } = useListChannelsQuery();
  const {
    data: programming,
    isLoading: isLoadingProgramming,
    error: programmingError,
  } = useGetChannelProgrammingQuery(
    { channelId: channelId || '' },
    { skip: !channelId },
  );

  const [createRotation] = useCreateChannelRotationMutation();
  const [deleteRotation] = useDeleteChannelRotationMutation();
  const [addRotationTrack] = useAddChannelRotationTrackMutation();
  const [removeRotationTrack] = useRemoveChannelRotationTrackMutation();
  const [moveRotationTrack] = useMoveChannelRotationTrackMutation();
  const [updateRotation] = useUpdateChannelRotationMutation();
  const [updateSchedule] = useUpdateChannelScheduleMutation();
  const [cancelSchedule] = useCancelChannelScheduleMutation();
  const [goLiveSchedule] = useGoLiveChannelScheduleMutation();

  const channels = React.useMemo(
    () =>
      (channelsData?.items || []).map((ch) => ({
        id: ch.channelId,
        name: ch.name,
        description: ch.description || '',
        status: toUiStatus(ch.status),
        lastUpdated: formatChannelUpdatedAt(ch.updatedAt),
        artwork: ch.artworkUrl || DEFAULT_ALBUM_ART,
      })),
    [channelsData],
  );

  const channelFromList = channels.find((c) => c.id === channelId);
  const channelFromProgramming = programming?.channel
    ? {
        id: programming.channel.channelId,
        name: programming.channel.name,
        description: programming.channel.description || '',
        status: toUiStatus(programming.channel.status),
        lastUpdated: formatChannelUpdatedAt(programming.channel.updatedAt),
        artwork: programming.channel.artworkUrl || DEFAULT_ALBUM_ART,
      }
    : null;
  const displayChannel = channelFromProgramming || channelFromList;

  const [songSearch, setSongSearch] = React.useState('');
  const [libraryKindFilter, setLibraryKindFilter] =
    React.useState<LibraryKindFilter>('all');
  /** Not In Rotation Schedule — current Staging set only. */
  const [libraryNirsOnly, setLibraryNirsOnly] = React.useState(false);
  const [draggedSong, setDraggedSong] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalRotationId, setModalRotationId] = React.useState<string | null>(null);
  const [modalSource, setModalSource] = React.useState<'staging' | 'production' | 'pending'>('staging');
  const [selectedSongs, setSelectedSongs] = React.useState<string[]>([]);
  const [moveTarget, setMoveTarget] = React.useState<string | undefined>(undefined);
  const [showMoveConfirm, setShowMoveConfirm] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [songToDelete, setSongToDelete] = React.useState<string | null>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = React.useState(false);
  const [duplicateSongNotice, setDuplicateSongNotice] =
    React.useState<DuplicateSongNotice | null>(null);
  const [addRotationModalOpen, setAddRotationModalOpen] = React.useState(false);
  const [addRotationStep, setAddRotationStep] = React.useState(1);
  const [addRotationType, setAddRotationType] = React.useState<'schedule' | 'rotation' | null>(null);
  const [newRotationName, setNewRotationName] = React.useState('');
  const [newScheduleName, setNewScheduleName] = React.useState('');
  const [scheduleFormError, setScheduleFormError] = React.useState<string | null>(
    null,
  );
  const [deleteRotationConfirmOpen, setDeleteRotationConfirmOpen] = React.useState(false);
  const [rotationToDelete, setRotationToDelete] = React.useState<string | null>(null);
  const [deleteLiveRotationConfirmOpen, setDeleteLiveRotationConfirmOpen] = React.useState(false);
  const [liveRotationToDelete, setLiveRotationToDelete] = React.useState<string | null>(null);
  const [goLiveModalOpen, setGoLiveModalOpen] = React.useState(false);
  const [selectedStagingSchedule, setSelectedStagingSchedule] = React.useState('A');
  const [extraLocalSets, setExtraLocalSets] = React.useState<string[]>([]);
  const [productionViewMode, setProductionViewMode] = React.useState<'live' | 'update'>('live');
  const [selectedLiveSchedule, setSelectedLiveSchedule] = React.useState('A');
  const [renameRotationOpen, setRenameRotationOpen] = React.useState(false);
  const [renameRotationId, setRenameRotationId] = React.useState<string | null>(null);
  const [renameRotationDraft, setRenameRotationDraft] = React.useState('');
  const [renameRotationBusy, setRenameRotationBusy] = React.useState(false);
  const [deleteScheduleModalOpen, setDeleteScheduleModalOpen] = React.useState(false);
  const [scheduleToDelete, setScheduleToDelete] = React.useState<string | null>(null);
  const [deleteLiveScheduleModalOpen, setDeleteLiveScheduleModalOpen] = React.useState(false);
  const [liveScheduleToDelete, setLiveScheduleToDelete] = React.useState<string | null>(null);
  const [selectedRotation, setSelectedRotation] = React.useState<string | null>(null);
  const [rotationModalOpen, setRotationModalOpen] = React.useState(false);
  const [dndKey, setDndKey] = React.useState(0);
  const [waveformModalOpen, setWaveformModalOpen] = React.useState(false);
  const [waveformSong, setWaveformSong] = React.useState<SongRecord | null>(null);
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const [wavesurfer, setWavesurfer] = React.useState<any>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [channelDropdownOpen, setChannelDropdownOpen] = React.useState(false);
  const [channelCardFlipped, setChannelCardFlipped] = React.useState(false);

  const cdnBaseUrl = getMusicApprovedCdnBaseUrl();
  const {
    data: canonicalTracksData,
    isLoading: isLoadingSongs,
    error: tracksError,
    refetch: refetchTracks,
  } = useListTracksQuery({
    status: 'active',
    limit: 200,
  });
  const { data: artistsData } = useListArtistsQuery({ status: 'active', limit: 200 });

  const librarySongs: SongRecord[] = React.useMemo(() => {
    const artistNames = new Map<string, string>();
    (artistsData?.artists || []).forEach(a => artistNames.set(a.artistId, a.name));
    return (canonicalTracksData?.tracks || [])
      .filter(track => track.playable === true && track.approvedObjectKey)
      .map(track => ({
        key: track.trackId,
        name: `${
          (track.primaryArtistId && artistNames.get(track.primaryArtistId)) || 'Unknown Artist'
        } - ${track.title || formatEntityRef('track', track.trackId)}`,
        album: '',
        recordLabel: '',
        isrc: track.isrc || '',
        duration: '00:00',
        kkExclusive: track.songType === 'kk-original' ? 'YES' : 'NO',
        processed: 'YES',
        songType: track.songType,
        streamUrl:
          cdnBaseUrl && track.approvedObjectKey
            ? `${cdnBaseUrl}/${track.approvedObjectKey}`
            : undefined,
      }));
  }, [canonicalTracksData, artistsData, cdnBaseUrl]);

  const songsWithUploadInfo = librarySongs;
  const libraryById = React.useMemo(() => {
    const map = new Map<string, SongRecord>();
    for (const s of librarySongs) map.set(s.key, s);
    return map;
  }, [librarySongs]);

  const stagingSchedules = React.useMemo(
    () => groupStagingBySet(programming?.staging, extraLocalSets),
    [programming?.staging, extraLocalSets],
  );

  const stagingRotations = React.useMemo(
    () => stagingSchedules[selectedStagingSchedule] || [],
    [stagingSchedules, selectedStagingSchedule],
  );
  const stagingSongsByRotation = React.useMemo(
    () => songsByRotation(stagingRotations),
    [stagingRotations],
  );

  const pendingProductionSchedule = React.useMemo(() => {
    if (!programming?.pending) return null;
    const setName =
      parseRotationSetKey(
        programming.pending.schedule.rotationSet ||
          selectedStagingSchedule ||
          'A',
      ) || 'A';
    return {
      name: setName,
      rotations: rotationsFromSchedule(programming.pending),
    };
  }, [programming?.pending, selectedStagingSchedule]);

  const productionRotations = React.useMemo(
    () => rotationsFromSchedule(programming?.production),
    [programming?.production],
  );
  const productionSongsByRotation = React.useMemo(
    () => songsByRotation(productionRotations),
    [productionRotations],
  );
  const pendingSongsByRotation = React.useMemo(
    () => songsByRotation(pendingProductionSchedule?.rotations || []),
    [pendingProductionSchedule],
  );

  const productionSetName =
    parseRotationSetKey(
      programming?.production?.schedule?.rotationSet ||
        selectedLiveSchedule ||
        'A',
    ) || 'A';

  const liveProductionSchedules = React.useMemo(() => {
    const map: Record<string, { name: string; rotations: UiRotation[] }> = {};
    for (const set of Object.keys(stagingSchedules)) {
      map[set] = { name: set, rotations: set === productionSetName ? productionRotations : [] };
    }
    if (!map[productionSetName]) {
      map[productionSetName] = { name: productionSetName, rotations: productionRotations };
    }
    return map;
  }, [stagingSchedules, productionSetName, productionRotations]);

  // Alias used by existing JSX for live song counts
  const liveRotations = productionRotations;

  const previewStagingRotations = React.useMemo(
    () => toPreviewRotations(stagingRotations),
    [stagingRotations],
  );
  const previewProductionRotations = React.useMemo(() => {
    const fromLive =
      liveProductionSchedules[selectedLiveSchedule]?.rotations ||
      (selectedLiveSchedule === productionSetName ? productionRotations : []);
    return toPreviewRotations(fromLive);
  }, [
    liveProductionSchedules,
    selectedLiveSchedule,
    productionSetName,
    productionRotations,
  ]);

  const streamUrlByTrackId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const song of librarySongs) {
      if (song.streamUrl) map.set(song.key, song.streamUrl);
    }
    return map;
  }, [librarySongs]);

  const channelPreview = useChannelPreviewPlayback({
    channelId,
    stagingRotations: previewStagingRotations,
    productionRotations: previewProductionRotations,
    resolveStreamUrl: (trackId) => streamUrlByTrackId.get(trackId) || null,
    active: channelCardFlipped,
  });

  React.useEffect(() => {
    setChannelCardFlipped(false);
    setChannelDropdownOpen(false);
  }, [channelId]);

  React.useEffect(() => {
    if (programming?.pending) {
      setProductionViewMode('update');
      if (programming.pending.schedule.rotationSet) {
        const key =
          parseRotationSetKey(programming.pending.schedule.rotationSet) || 'A';
        setSelectedLiveSchedule(key);
      }
    }
  }, [programming?.pending?.schedule?.scheduleId]);

  React.useEffect(() => {
    if (programming?.production?.schedule?.rotationSet) {
      const key =
        parseRotationSetKey(programming.production.schedule.rotationSet) || 'A';
      setSelectedLiveSchedule(key);
    }
  }, [programming?.production?.schedule?.scheduleId]);

  React.useEffect(() => {
    if (programmingError) {
      toast.error(extractChannelErrorMessage(programmingError));
    }
  }, [programmingError]);

  const lastStagingUpdate = React.useMemo(() => {
    const iso = programming?.staging?.schedule?.createdAt || programming?.channel?.updatedAt;
    return iso ? new Date(iso) : new Date();
  }, [programming]);

  const lastLiveUpdate = React.useMemo(() => {
    const iso =
      programming?.pending?.schedule?.becamePendingAt ||
      programming?.production?.schedule?.becameProductionAt ||
      programming?.production?.schedule?.createdAt ||
      programming?.channel?.updatedAt;
    return iso ? new Date(iso) : new Date();
  }, [programming]);

  const sensors = useSensors(useSensor(PointerSensor));

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.channel-dropdown-container')) {
        setChannelDropdownOpen(false);
      }
    };
    if (channelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [channelDropdownOpen]);

  React.useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  React.useEffect(() => {
    if (!wavesurfer) return;
    const updateTime = () => setCurrentTime(wavesurfer.getCurrentTime());
    const updateDuration = () => setDuration(wavesurfer.getDuration());
    wavesurfer.on('audioprocess', updateTime);
    wavesurfer.on('seek', updateTime);
    wavesurfer.on('ready', updateDuration);
    return () => {
      wavesurfer.un('audioprocess', updateTime);
      wavesurfer.un('seek', updateTime);
      wavesurfer.un('ready', updateDuration);
    };
  }, [wavesurfer]);

  React.useEffect(() => {
    if (wavesurfer && audioUrl) {
      setTimeout(() => {
        setDuration(wavesurfer.getDuration());
      }, 100);
    }
  }, [audioUrl, wavesurfer]);

  const trackIdsInCurrentStagingSchedule = React.useMemo(() => {
    const ids = new Set<string>();
    for (const songs of Object.values(stagingSongsByRotation)) {
      for (const s of songs) {
        if (s.trackId) ids.add(s.trackId);
      }
    }
    return ids;
  }, [stagingSongsByRotation]);

  const filteredSongs = React.useMemo(() => {
    const q = songSearch.trim().toLowerCase();
    return songsWithUploadInfo.filter((song) => {
      if (q && !song.name.toLowerCase().includes(q)) return false;
      if (libraryKindFilter === 'commercial' && song.songType !== 'commercial') {
        return false;
      }
      if (libraryKindFilter === 'original' && song.songType !== 'kk-original') {
        return false;
      }
      if (libraryNirsOnly && trackIdsInCurrentStagingSchedule.has(song.key)) {
        return false;
      }
      return true;
    });
  }, [
    songsWithUploadInfo,
    songSearch,
    libraryKindFilter,
    libraryNirsOnly,
    trackIdsInCurrentStagingSchedule,
  ]);

  const totalStagingSongs = stagingRotations.reduce(
    (acc, rotation) => acc + (stagingSongsByRotation[rotation.id]?.length || 0),
    0,
  );
  const totalLiveSongs = productionRotations.reduce(
    (acc, rotation) => acc + (productionSongsByRotation[rotation.id]?.length || 0),
    0,
  );
  const pendingTotalSongs = pendingProductionSchedule
    ? pendingProductionSchedule.rotations.reduce(
        (acc, rotation) => acc + (pendingSongsByRotation[rotation.id]?.length || 0),
        0,
      )
    : 0;
  const pendingRotationsCount = pendingProductionSchedule ? pendingProductionSchedule.rotations.length : 0;

  const stagingIdSet = React.useMemo(
    () => new Set(stagingRotations.map((r) => r.id)),
    [stagingRotations],
  );
  const productionIdSet = React.useMemo(
    () => new Set(productionRotations.map((r) => r.id)),
    [productionRotations],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const id = String(active.id);
    if (id.startsWith('song-')) {
      const trackId = id.replace('song-', '');
      setDraggedSong(libraryById.get(trackId)?.name || trackId);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedSong(null);
    if (!over || active.id === over.id) {
      const activeId = String(active.id);
      if (stagingIdSet.has(activeId)) {
        const target = event.activatorEvent?.target as HTMLElement;
        if (target?.closest('.delete-icon')) {
          handleDeleteRotation(activeId);
          return;
        }
        handleOpenRotationModal(activeId, 'staging');
        return;
      }
      if (productionIdSet.has(activeId)) {
        handleOpenRotationModal(activeId, 'production');
        return;
      }
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith('song-') && channelId) {
      const trackId = activeId.replace('song-', '');
      const song = libraryById.get(trackId);
      if (!song) return;
      if (stagingIdSet.has(overId)) {
        // Owner 2026-07-31: never add the same song twice in a rotation or
        // anywhere else in the selected rotation schedule.
        const existingIndex = stagingRotations.findIndex((r) =>
          (stagingSongsByRotation[r.id] || []).some((t) => t.trackId === trackId),
        );
        if (existingIndex >= 0) {
          setDuplicateSongNotice(
            buildSongAlreadyInScheduleNotice(
              song.name,
              stagingRotations[existingIndex],
              existingIndex,
            ),
          );
          setDuplicateModalOpen(true);
          return;
        }
        const artistName = song.name.includes(' - ')
          ? song.name.split(' - ').slice(0, -1).join(' - ')
          : undefined;
        const trackTitle = song.name.includes(' - ')
          ? song.name.split(' - ').slice(-1)[0]
          : song.name;
        try {
          await addRotationTrack({
            channelId,
            rotationId: overId,
            trackId,
            trackTitle,
            artistName,
          }).unwrap();
        } catch (err) {
          const apiMsg = extractChannelErrorMessage(err);
          // Prefer a located message when the client can still resolve the slot.
          const locatedIndex = stagingRotations.findIndex((r) =>
            (stagingSongsByRotation[r.id] || []).some((t) => t.trackId === trackId),
          );
          setDuplicateSongNotice(
            locatedIndex >= 0
              ? buildSongAlreadyInScheduleNotice(
                  song.name,
                  stagingRotations[locatedIndex],
                  locatedIndex,
                )
              : {
                  headline:
                    apiMsg ||
                    `${song.name} is already in use in this Rotation Schedule`,
                  locationLabel: null,
                  locationRotationId: null,
                },
          );
          setDuplicateModalOpen(true);
        }
        return;
      }
      // Production drops are not mutable via API — ignore (locked UI may still accept drop highlight).
      return;
    }

    if (stagingIdSet.has(activeId) && stagingIdSet.has(overId) && channelId) {
      const oldIndex = stagingRotations.findIndex(r => r.id === activeId);
      const newIndex = stagingRotations.findIndex(r => r.id === overId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const reordered = arrayMove(stagingRotations, oldIndex, newIndex);
      // Only PATCH rows whose order actually changes — the API rejects no-ops
      // with "Body must contain at least one of: name, categoryLabel, order".
      const patches = reordered
        .map((r, idx) => ({ rotationId: r.id, order: idx + 1, prev: r.order }))
        .filter((p) => p.prev !== p.order);
      if (patches.length === 0) return;
      try {
        await Promise.all(
          patches.map((p) =>
            updateRotation({
              channelId,
              rotationId: p.rotationId,
              order: p.order,
            }).unwrap(),
          ),
        );
      } catch (err) {
        toast.error(extractChannelErrorMessage(err));
      }
      return;
    }
  };

  const handleOpenRotationModal = (
    rotationId: string,
    source: 'staging' | 'production' | 'pending' = 'staging',
  ) => {
    setModalRotationId(rotationId);
    setModalSource(source);
    setSelectedSongs([]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalRotationId(null);
    setSelectedSongs([]);
  };

  const modalSongObjects: ModalSong[] = React.useMemo(() => {
    if (!modalRotationId) return [];
    if (modalSource === 'production') return productionSongsByRotation[modalRotationId] || [];
    if (modalSource === 'pending') return pendingSongsByRotation[modalRotationId] || [];
    return stagingSongsByRotation[modalRotationId] || [];
  }, [modalRotationId, modalSource, productionSongsByRotation, pendingSongsByRotation, stagingSongsByRotation]);

  const handleSongCheckbox = (rotationTrackId: string) => {
    setSelectedSongs((prev) =>
      prev.includes(rotationTrackId)
        ? prev.filter((s) => s !== rotationTrackId)
        : [...prev, rotationTrackId]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && modalRotationId) {
      setSelectedSongs(modalSongObjects.map((s) => s.rotationTrackId));
    } else {
      setSelectedSongs([]);
    }
  };

  const handleMoveTargetChange = (value: string) => {
    setMoveTarget(value);
  };

  const handleMoveSongs = () => {
    setShowMoveConfirm(true);
  };

  const handleConfirmMove = async () => {
    if (!channelId || !modalRotationId || !moveTarget || selectedSongs.length === 0) return;
    try {
      for (const rotationTrackId of selectedSongs) {
        await moveRotationTrack({
          channelId,
          rotationId: modalRotationId,
          rotationTrackId,
          toRotationId: moveTarget,
        }).unwrap();
      }
      setSelectedSongs([]);
      setMoveTarget(undefined);
      setShowMoveConfirm(false);
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleCancelMove = () => {
    setShowMoveConfirm(false);
  };

  const handleRemoveSong = (rotationTrackId: string) => {
    setSongToDelete(rotationTrackId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteSong = async () => {
    if (channelId && modalRotationId && songToDelete) {
      try {
        await removeRotationTrack({
          channelId,
          rotationId: modalRotationId,
          rotationTrackId: songToDelete,
        }).unwrap();
        setSelectedSongs((prev) => prev.filter((s) => s !== songToDelete));
      } catch (err) {
        toast.error(extractChannelErrorMessage(err));
      }
    }
    setDeleteConfirmOpen(false);
    setSongToDelete(null);
  };

  const handleCancelDeleteSong = () => {
    setDeleteConfirmOpen(false);
    setSongToDelete(null);
  };

  const allSelected = modalSongObjects.length > 0 && selectedSongs.length === modalSongObjects.length;
  const moveOptions = stagingRotations
    .filter((r) => r.id !== modalRotationId)
    .map((r, filteredIdx) => {
      const idx = stagingRotations.findIndex((x) => x.id === r.id);
      const slotIdx = idx >= 0 ? idx : filteredIdx;
      // Match main Staging column: "Rotation A01" + category name + count
      const slot =
        r.labelName?.trim() ||
        `Rotation ${selectedStagingSchedule}${String(slotIdx + 1).padStart(2, '0')}`;
      const count = stagingSongsByRotation[r.id]?.length || 0;
      return {
        label: `${slot}: ${r.name} (${count})`,
        value: r.id,
      };
    });
  const moveTargetLabel =
    moveOptions.find((o) => o.value === moveTarget)?.label ||
    stagingRotations.find((x) => x.id === moveTarget)?.name ||
    '';

  const handleAddRotation = async () => {
    if (!channelId) return;
    if (addRotationType === 'rotation' && newRotationName.trim()) {
      const categoryLabel = newRotationName.trim();
      if (categoryLabel.length > MAX_CATEGORY_LABEL_LEN) {
        toast.error(`Rotation name exceeds ${MAX_CATEGORY_LABEL_LEN} characters`);
        return;
      }
      const rotationSet =
        parseRotationSetKey(selectedStagingSchedule) || selectedStagingSchedule;
      if (!parseRotationSetKey(rotationSet)) {
        toast.error(
          `Rotation schedule key exceeds ${MAX_ROTATION_SET_LEN} characters. Pick or add a shorter schedule key.`,
        );
        return;
      }
      try {
        await createRotation({
          channelId,
          rotationSet,
          categoryLabel,
        }).unwrap();
        setNewRotationName('');
        setAddRotationModalOpen(false);
        setAddRotationStep(1);
        setAddRotationType(null);
      } catch (err) {
        toast.error(extractChannelErrorMessage(err));
      }
    } else if (addRotationType === 'schedule' && newScheduleName.trim()) {
      // Local-only empty set until a rotation is created against it (no create-schedule endpoint).
      // API rotationSet is a short key (≤8); free-form names belong on each rotation (categoryLabel).
      const key = parseRotationSetKey(newScheduleName);
      if (!key) {
        setScheduleFormError(
          `Schedule key must be 1–${MAX_ROTATION_SET_LEN} characters (e.g. D, Fest, or “Rotation D”).`,
        );
        return;
      }
      const alreadyExists = Object.keys(stagingSchedules).some(
        (existing) => existing.toLowerCase() === key.toLowerCase(),
      );
      if (alreadyExists) {
        setScheduleFormError(`${rotationSetLabel(key)} already exists`);
        return;
      }
      setExtraLocalSets((prev) => (prev.includes(key) ? prev : [...prev, key]));
      setSelectedStagingSchedule(key);
      setNewScheduleName('');
      setScheduleFormError(null);
      setAddRotationModalOpen(false);
      setAddRotationStep(1);
      setAddRotationType(null);
    }
  };

  const handleOpenRenameRotation = (rotation: UiRotation, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setRenameRotationId(rotation.id);
    setRenameRotationDraft(rotation.name);
    setRenameRotationOpen(true);
  };

  const handleCancelRenameRotation = () => {
    if (renameRotationBusy) return;
    setRenameRotationOpen(false);
    setRenameRotationId(null);
    setRenameRotationDraft('');
  };

  const handleConfirmRenameRotation = async () => {
    if (!channelId || !renameRotationId) return;
    const categoryLabel = renameRotationDraft.trim();
    if (!categoryLabel) {
      toast.error('Rotation name is required');
      return;
    }
    if (categoryLabel.length > MAX_CATEGORY_LABEL_LEN) {
      toast.error(`Rotation name exceeds ${MAX_CATEGORY_LABEL_LEN} characters`);
      return;
    }
    setRenameRotationBusy(true);
    try {
      await updateRotation({
        channelId,
        rotationId: renameRotationId,
        categoryLabel,
      }).unwrap();
      setRenameRotationOpen(false);
      setRenameRotationId(null);
      setRenameRotationDraft('');
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    } finally {
      setRenameRotationBusy(false);
    }
  };

  const handleOpenAddRotationModal = () => {
    setAddRotationModalOpen(true);
    setAddRotationStep(1);
    setAddRotationType(null);
    setNewRotationName('');
    setNewScheduleName('');
    setScheduleFormError(null);
  };

  const handleCancelAddRotationModal = () => {
    setAddRotationModalOpen(false);
    setAddRotationStep(1);
    setAddRotationType(null);
    setNewRotationName('');
    setNewScheduleName('');
    setScheduleFormError(null);
  };

  const handleDeleteRotation = (rotationId: string) => {
    setRotationToDelete(rotationId);
    setDeleteRotationConfirmOpen(true);
  };

  const handleConfirmDeleteRotation = async () => {
    if (channelId && rotationToDelete) {
      try {
        await deleteRotation({ channelId, rotationId: rotationToDelete }).unwrap();
      } catch (err) {
        toast.error(extractChannelErrorMessage(err));
      }
    }
    setDeleteRotationConfirmOpen(false);
    setRotationToDelete(null);
  };

  const handleCancelDeleteRotation = () => {
    setDeleteRotationConfirmOpen(false);
    setRotationToDelete(null);
  };

  const handleRotationClick = (rotationName: string) => {
    setSelectedRotation(rotationName);
    setRotationModalOpen(true);
  };

  const handleDeleteLiveRotation = (rotationId: string) => {
    setLiveRotationToDelete(rotationId);
    setDeleteLiveRotationConfirmOpen(true);
  };

  const handleConfirmDeleteLiveRotation = () => {
    // Production rotations are not deleted from this surface (C2); keep confirm UX but no-op API.
    setDeleteLiveRotationConfirmOpen(false);
    setLiveRotationToDelete(null);
  };

  const handleCancelDeleteLiveRotation = () => {
    setDeleteLiveRotationConfirmOpen(false);
    setLiveRotationToDelete(null);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const handleStagingRotationScheduleChange = (value: string) => {
    setSelectedStagingSchedule(value);
  };

  const handleLoadFromStaging = async (scheduleName: string) => {
    if (!channelId) return;
    const rotationSet = parseRotationSetKey(scheduleName);
    if (!rotationSet) {
      toast.error(
        `Rotation schedule key exceeds ${MAX_ROTATION_SET_LEN} characters`,
      );
      return;
    }
    try {
      await updateSchedule({ channelId, rotationSet }).unwrap();
      setProductionViewMode('update');
    } catch (err) {
      const msg = extractChannelErrorMessage(err);
      setDuplicateSongNotice({
        headline: msg,
        locationLabel: null,
        locationRotationId: null,
      });
      setDuplicateModalOpen(true);
    }
  };

  const handleGoLiveConfirm = async () => {
    if (!channelId) return;
    try {
      await goLiveSchedule({ channelId }).unwrap();
      setGoLiveModalOpen(false);
      setProductionViewMode('live');
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleOpenDeleteScheduleModal = (scheduleName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setScheduleToDelete(scheduleName);
    setDeleteScheduleModalOpen(true);
  };

  const handleConfirmDeleteSchedule = () => {
    if (scheduleToDelete) {
      setExtraLocalSets((prev) => prev.filter((s) => s !== scheduleToDelete));
      if (selectedStagingSchedule === scheduleToDelete) {
        const remaining = Object.keys(stagingSchedules).filter(s => s !== scheduleToDelete);
        setSelectedStagingSchedule(remaining[0] || 'A');
      }
    }
    setDeleteScheduleModalOpen(false);
    setScheduleToDelete(null);
  };

  const handleCancelDeleteSchedule = () => {
    setDeleteScheduleModalOpen(false);
    setScheduleToDelete(null);
  };

  const handleOpenDeleteLiveScheduleModal = (scheduleName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setLiveScheduleToDelete(scheduleName);
    setDeleteLiveScheduleModalOpen(true);
  };

  const handleConfirmDeleteLiveSchedule = () => {
    setDeleteLiveScheduleModalOpen(false);
    setLiveScheduleToDelete(null);
  };

  const handleCancelDeleteLiveSchedule = () => {
    setDeleteLiveScheduleModalOpen(false);
    setLiveScheduleToDelete(null);
  };

  function resetAfterCancelPendingSchedule() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.classList.remove('ant-modal-open');
  }

  const handleCancelPending = async () => {
    if (!channelId) return;
    try {
      await cancelSchedule({ channelId }).unwrap();
      setProductionViewMode('update');
      resetAfterCancelPendingSchedule();
      setModalOpen(false);
      setModalRotationId(null);
      setDndKey(k => k + 1);
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleSongWaveformClick = (song: SongRecord) => {
    setWaveformSong(song);
    setWaveformModalOpen(true);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (song.streamUrl) {
      setAudioUrl(song.streamUrl);
    } else {
      setAudioUrl(null);
    }
  };

  const handleModalSongWaveformClick = (song: ModalSong) => {
    const fromLibrary = libraryById.get(song.trackId);
    if (!fromLibrary?.streamUrl) {
      toast.error('No playable stream URL for this song');
      return;
    }
    handleSongWaveformClick(fromLibrary);
  };

  const handleCloseWaveformModal = () => {
    setWaveformModalOpen(false);
    setWaveformSong(null);
    setIsPlaying(false);
    if (wavesurfer) {
      wavesurfer.stop();
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  const handlePlayPause = () => {
    if (wavesurfer) {
      if (isPlaying) {
        wavesurfer.pause();
        setIsPlaying(false);
      } else {
        wavesurfer.play();
        setIsPlaying(true);
      }
    }
  };

  const formatTime = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Retain locked-UI handlers/modals that are not yet API-backed entry points.
  void handleRotationClick;
  void handleDeleteLiveRotation;
  void handleOpenDeleteScheduleModal;
  void handleOpenDeleteLiveScheduleModal;
  void liveRotationToDelete;

  // No more JavaScript - just pure inline styles

  if (!channelId) {
    navigate('/channel-programming-overview');
    return null;
  }

  if (isLoadingChannels || isLoadingProgramming) {
    return (
      <Container fluid>
        <Card style={{ borderRadius: 12 }}>
          <Card.Body style={{ textAlign: 'center', padding: 48 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // If no channel is selected, redirect to overview page
  if (!displayChannel) {
    navigate('/channel-programming-overview');
    return null;
  }
  
  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Channel Programming Interface" />
            <div style={{ textAlign: 'right', minWidth: 260 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>
                Channel status: <span style={{ color: '#222', fontWeight: 700 }}>{displayChannel.status}</span>
                <Tooltip title="This channel is eligible to go live.">
                  <InfoCircleOutlined style={{ color: '#222', marginLeft: 6 }} />
                </Tooltip>
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Last updated: {displayChannel.lastUpdated}
              </div>
            </div>
          </Card.Title>
          
          <DndContext key={dndKey} sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Row gutter={24}>
          {/* Left: Channel Info & Song Library */}
          <Col span={6}>
            <Card
              className={styles.rounded12}
              style={{
                marginBottom: 24,
                padding: 0,
                // visible so the channel switcher dropdown is not clipped
                overflow: channelDropdownOpen ? 'visible' : 'hidden',
                position: 'relative',
                zIndex: channelDropdownOpen ? 20 : 'auto',
              }}
            >
              <div
                className={`${styles.channelFlip}${channelCardFlipped ? ` ${styles.channelFlipFlipped}` : ''}`}
              >
                <div className={styles.channelFlipInner}>
                  <div
                    className={styles.channelFlipFront}
                    style={{ padding: 12, paddingBottom: 10, position: 'relative' }}
                    onClick={() => setChannelCardFlipped(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setChannelCardFlipped(true);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Show channel preview controls"
                  >
                    <div style={{ marginBottom: 8, textAlign: 'left', width: '100%' }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#2c3e50', marginBottom: 4 }}>
                        {displayChannel.name}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: '#888',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%',
                      }}>
                        {displayChannel.description}
                      </div>
                    </div>

                    <div className={styles.flexCenter} style={{ marginTop: 0 }}>
                      <img
                        src={getChannelArtwork(displayChannel.artwork)}
                        alt="artwork"
                        style={{ width: 70, height: 70, borderRadius: 8, objectFit: 'cover', marginRight: 16 }}
                      />
                      <div className={styles.flex1}>
                        <div>
                          <div
                            style={{ overflow: 'visible', width: '140px', position: 'relative' }}
                            className="channel-dropdown-container"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              style={{
                                width: '100%',
                                maxWidth: '140px',
                                fontSize: 10,
                                padding: '8px 10px',
                                paddingRight: '28px',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                                outline: 'none',
                                color: '#2c3e50',
                                fontWeight: 600,
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setChannelDropdownOpen(!channelDropdownOpen);
                              }}
                            >
                              <span>{displayChannel.name}</span>
                              <CaretDownOutlined style={{ fontSize: 10, color: '#7f8c8d' }} />
                            </button>
                            {channelDropdownOpen && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  width: '200px',
                                  background: '#ffffff',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  zIndex: 9999,
                                  marginTop: '4px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  minHeight: '100px',
                                }}
                              >
                                <div style={{
                                  padding: '8px',
                                  color: '#666',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  borderBottom: '1px solid #f0f0f0',
                                }}>
                                  Select a channel
                                </div>
                                {channels.map((c) => (
                                  <div
                                    key={`${c.id}-${displayChannel.id}`}
                                    className={`dropdown-item-no-bg ${styles.channelDropdownItem}`}
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      fontSize: 10,
                                      color: c.id === displayChannel.id ? '#e21e26' : '#2c3e50',
                                      fontWeight: c.id === displayChannel.id ? 600 : 400,
                                      background: '#ffffff',
                                      borderBottom: '1px solid #f0f0f0',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/channel-programming/${c.id}`);
                                      setChannelDropdownOpen(false);
                                    }}
                                  >
                                    <img
                                      src={getChannelArtwork(c.artwork)}
                                      alt={c.name}
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        marginRight: 8,
                                      }}
                                    />
                                    <span style={{ flex: 1 }}>{c.name}</span>
                                    {c.id === displayChannel.id && (
                                      <PlayCircleOutlined style={{ fontSize: 10, color: '#e21e26' }} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.channelFlipHint}>Click card to preview channel audio</div>
                  </div>

                  <div
                    className={styles.channelFlipBack}
                    aria-hidden={!channelCardFlipped}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={styles.previewSourceLabel}>Listen to</div>
                    <div className={styles.previewSourceToggle} role="group" aria-label="Preview schedule source">
                      <button
                        type="button"
                        className={`${styles.previewSourceOption}${channelPreview.source === 'staging' ? ` ${styles.previewSourceOptionActive}` : ''}`}
                        onClick={() => channelPreview.changeSource('staging')}
                        aria-pressed={channelPreview.source === 'staging'}
                        title="Staging schedule"
                      >
                        Staging
                      </button>
                      <button
                        type="button"
                        className={`${styles.previewSourceOption}${channelPreview.source === 'production' ? ` ${styles.previewSourceOptionActive}` : ''}`}
                        onClick={() => channelPreview.changeSource('production')}
                        aria-pressed={channelPreview.source === 'production'}
                        title="Production schedule"
                      >
                        Production
                      </button>
                    </div>
                    <div className={styles.previewNowPlaying}>
                      {channelPreview.current ? (
                        <>
                          <div className={styles.previewNowPlayingTitle}>
                            {channelPreview.current.label}
                          </div>
                          <div className={styles.previewNowPlayingMeta}>
                            {channelPreview.current.slotLabel}
                            {channelPreview.current.categoryLabel
                              ? ` · ${channelPreview.current.categoryLabel}`
                              : ''}
                          </div>
                        </>
                      ) : (
                        <div className={styles.previewEmpty}>
                          {channelPreview.playable
                            ? `Ready — ${channelPreview.source === 'staging' ? 'Staging' : 'Production'} schedule`
                            : `No playable songs in ${channelPreview.source === 'staging' ? 'Staging' : 'Production'} schedule`}
                        </div>
                      )}
                    </div>
                    <div className={styles.previewControls}>
                      <button
                        type="button"
                        className={styles.previewControlBtn}
                        disabled={!channelPreview.playable}
                        aria-label={channelPreview.isPlaying ? 'Pause' : 'Play'}
                        onClick={() => {
                          if (channelPreview.isPlaying) {
                            channelPreview.pause();
                          } else {
                            void channelPreview.play();
                          }
                        }}
                      >
                        {channelPreview.isPlaying ? (
                          <PauseCircleOutlined />
                        ) : (
                          <PlayCircleOutlined />
                        )}
                      </button>
                      <button
                        type="button"
                        className={styles.previewControlBtn}
                        disabled={!channelPreview.playable}
                        aria-label="Skip to next rotation"
                        onClick={() => {
                          void channelPreview.skip();
                        }}
                      >
                        <StepForwardOutlined />
                      </button>
                    </div>
                    <button
                      type="button"
                      className={styles.previewFlipBack}
                      onClick={() => setChannelCardFlipped(false)}
                    >
                      Back to channel
                    </button>
                  </div>
                </div>
              </div>
            </Card>
            <Card className={`${styles.rounded12} ${styles.flexColumn}`} style={{ minHeight: 480, maxHeight: 600, overflow: 'hidden' }}>
              <div style={{ padding: 16, borderBottom: '1px solid #eee' }}>
                <div className={`${styles.flexCenter} ${styles.flexGap10}`}>
                  <img src={kkFavicon} alt="KrossKast" style={{ width: 24, height: 20, marginRight: 4 }} />
                  <span style={{ fontWeight: 600, fontSize: 15 }}>KrossKast Song Library</span>
                </div>
                <div className={styles.grayText} style={{ fontWeight: 400, fontSize: 13, marginTop: 2, textAlign: 'left', marginLeft: 40 }}>
                  {/* An API failure must never read as an empty catalog ("0 Songs"). */}
                  {tracksError
                    ? 'Library unavailable'
                    : `${filteredSongs.length.toLocaleString()} Song${filteredSongs.length === 1 ? '' : 's'}`}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginLeft: 0, marginTop: 14, paddingRight: 12 }}>
                  <button
                    type="button"
                    style={libraryFilterBtnStyle(libraryKindFilter === 'all')}
                    onClick={() => setLibraryKindFilter('all')}
                    aria-pressed={libraryKindFilter === 'all'}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    style={libraryFilterBtnStyle(libraryKindFilter === 'commercial')}
                    onClick={() => setLibraryKindFilter('commercial')}
                    aria-pressed={libraryKindFilter === 'commercial'}
                  >
                    Commercial
                  </button>
                  <button
                    type="button"
                    style={{
                      ...libraryFilterBtnStyle(libraryKindFilter === 'original'),
                      marginRight: 0,
                    }}
                    onClick={() => setLibraryKindFilter('original')}
                    aria-pressed={libraryKindFilter === 'original'}
                  >
                    Original
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 0, marginTop: 8, paddingRight: 12 }}>
                  <button
                    type="button"
                    title="Not In Rotation Schedule"
                    aria-label="Not In Rotation Schedule"
                    style={{
                      ...libraryFilterBtnStyle(libraryNirsOnly),
                      marginRight: 0,
                    }}
                    onClick={() => setLibraryNirsOnly((v) => !v)}
                    aria-pressed={libraryNirsOnly}
                  >
                    NIRS
                  </button>
                </div>
              </div>
              <div style={{ padding: 16, paddingBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Song search"
                  style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: '6px 12px', fontSize: 13 }}
                  value={songSearch}
                  onChange={e => setSongSearch(e.target.value)}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
                {tracksError ? (
                  <div
                    data-testid="cp-track-library-error"
                    style={{ textAlign: 'center', padding: '20px', color: '#a94442' }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Track library unavailable</div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                      The canonical Track catalog could not be loaded. This is a loading error, not
                      an empty library.
                    </div>
                    <button
                      type="button"
                      data-testid="cp-track-library-retry"
                      onClick={() => refetchTracks()}
                      style={{
                        fontSize: 12,
                        padding: '4px 14px',
                        borderRadius: 4,
                        border: '1px solid #a94442',
                        background: '#fff',
                        color: '#a94442',
                        cursor: 'pointer',
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : isLoadingSongs ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                    Loading songs...
                  </div>
                ) : filteredSongs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                    {songSearch || libraryKindFilter !== 'all' || libraryNirsOnly
                      ? 'No songs match the current filters'
                      : 'No approved songs in library'}
                  </div>
                ) : (
                  filteredSongs.map((song, idx) => (
                    <DraggableSong key={song.key || song.name} id={`song-${song.key}`}>
                      {({ attributes, listeners }) => (
                        <div style={{ display: 'flex', alignItems: 'center', background: '#f7f7f7', borderRadius: 7, padding: '8px 12px', marginBottom: 8, fontSize: 13, fontWeight: 500, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                          <span
                            style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'grab' }}
                            {...attributes}
                            {...listeners}
                            onClick={() => handleSongWaveformClick(song)}
                          >
                            {song.name}
                          </span>
                          <PlayCircleOutlined
                            className="song-play-icon"
                            style={{ fontSize: 20, color: '#e21e26', marginLeft: 10, cursor: 'pointer', flexShrink: 0 }}
                            title="Preview waveform"
                            onClick={e => {
                              e.stopPropagation();
                              handleSongWaveformClick(song);
                            }}
                          />
                        </div>
                      )}
                    </DraggableSong>
                  ))
                )}
              </div>
            </Card>
          </Col>
          {/* Middle: Staging Rotations */}
          <Col span={9}>
            <Card style={{ borderRadius: 12, minHeight: 530, maxHeight: 650, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: 16, borderBottom: '1px solid #eee', background: '#23272b', color: 'white', fontWeight: 600, fontSize: 15 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 15, color: 'white' }}>Staging Rotation Schedules</span>
                    <Select
                      style={{ marginTop: 8, minWidth: 140, borderRadius: 12, fontSize: 16 }}
                      value={selectedStagingSchedule}
                      options={Object.keys(stagingSchedules).map((key) => ({
                        label: rotationSetLabel(key),
                        value: key,
                      }))}
                      onChange={handleStagingRotationScheduleChange}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        color: 'white', 
                        fontWeight: 600, 
                        fontSize: 13, 
                        background: 'none', 
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        marginTop: 2
                      }}
                      onClick={handleOpenAddRotationModal}
                    >
                      Add new rotation<PlusOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </span>
                    <div style={{ color: '#bbb', fontWeight: 400, fontSize: 13, marginTop: 3, whiteSpace: 'nowrap' }}>
                      {totalStagingSongs} Songs | {stagingRotations.length} Rotations
                  </div>
                    <div style={{ color: '#bbb', fontWeight: 400, fontSize: 12, marginTop: 2 }}>
                      Updated {formatDate(lastStagingUpdate)}
                </div>
              </div>
                </div>
              </div>
              <SortableContext items={(stagingRotations || []).map(r => r.id)} strategy={verticalListSortingStrategy}>
                <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', paddingRight: 12, paddingLeft: 0, paddingTop: 0, paddingBottom: 0, marginTop: 12 }}>
                  {/* Fixed left column for Rotation A# labels */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 80, marginRight: 16, marginLeft: 16 }}>
                    {(stagingRotations || []).map((rotation, idx) => (
                      <div key={rotation.id} style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontWeight: 700, color: '#23272b', fontSize: 13, marginBottom: 10 }}>
                        {rotation.labelName || `Rotation A${(idx + 1).toString().padStart(2, '0')}`}
                      </div>
                    ))}
                  </div>
                  {/* Draggable/droppable rotation folders */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {(stagingRotations || []).map((rotation, idx) => (
                    <SortableRotation key={rotation.id} id={rotation.id}>
                        {(isDragging) => (
                          <DroppableRotation id={rotation.id} isDragging={isDragging}>
                            <div onClick={(e) => { e.stopPropagation(); handleOpenRotationModal(rotation.id, 'staging'); }} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <span
                                style={{ flex: 1 }}
                                title="Double-click to rename"
                                onDoubleClick={(e) => handleOpenRenameRotation(rotation, e)}
                              >
                                {rotation.name}{' '}
                                <span style={{ color: '#888', fontWeight: 400 }}>
                                  ({stagingSongsByRotation[rotation.id]?.length || 0})
                                </span>
                              </span>
                              <CloseOutlined 
                                className="delete-icon"
                                style={{ 
                                  color: '#888', 
                                  fontSize: 10, 
                                  cursor: 'pointer', 
                                  marginLeft: 10,
                                  opacity: 0.7,
                                  transition: 'opacity 0.2s'
                                }} 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteRotation(rotation.id);
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                              />
                            </div>
                      </DroppableRotation>
                        )}
                    </SortableRotation>
                  ))}
                  </div>
                </div>
              </SortableContext>
            </Card>
          </Col>
          {/* Right: Live Rotations Order */}
          <Col span={9}>
            <Card style={{ borderRadius: 12, minHeight: 530, maxHeight: 650, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: 16, borderBottom: '1px solid #eee', background: '#23272b', color: 'white', fontWeight: 600, fontSize: 15 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 15, color: 'white' }}>Production Rotation Schedules</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 1, marginTop: 8 }}>
                      {productionViewMode === 'live' ? (
                        <Select
                          style={{ minWidth: 160, borderRadius: 12, fontSize: 16 }}
                          value={selectedLiveSchedule}
                          options={Object.keys(liveProductionSchedules).map((key) => ({
                            label: rotationSetLabel(key),
                            value: key,
                          }))}
                          onChange={setSelectedLiveSchedule}
                        />
                      ) : (
                    <Select
                          style={{ minWidth: 160, borderRadius: 12, fontSize: 16 }}
                          placeholder="Load from Staging"
                          options={Object.keys(stagingSchedules).map((key) => ({
                            label: rotationSetLabel(key),
                            value: key,
                          }))}
                          onChange={handleLoadFromStaging}
                          value={pendingProductionSchedule ? pendingProductionSchedule.name : undefined}
                        />
                      )}
                  </div>
                    {productionViewMode === 'update' && pendingProductionSchedule && (
                      <div style={{ color: '#e21e26', fontWeight: 700, fontSize: 15, margin: '8px 0 0 0', textAlign: 'left' }}>
                        Pending Schedule
                </div>
                    )}
              </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
                      <button
                        style={{
                          padding: '1px 5px',
                          borderRadius: 4,
                          border: productionViewMode === 'live' ? '2px solid #1890ff' : '1px solid #bbb',
                          background: productionViewMode === 'live' ? '#e6f7ff' : '#fff',
                          color: productionViewMode === 'live' ? '#1890ff' : '#23272b',
                          fontWeight: 400,
                          fontSize: 9,
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => setProductionViewMode('live')}
                      >
                        Live
                      </button>
                      <button
                        style={{
                          padding: '1px 5px',
                          borderRadius: 4,
                          border: productionViewMode === 'update' ? '2px solid #e21e26' : '1px solid #bbb',
                          background: productionViewMode === 'update' ? '#fff0f0' : '#fff',
                          color: productionViewMode === 'update' ? '#e21e26' : '#23272b',
                          fontWeight: 400,
                          fontSize: 9,
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => setProductionViewMode('update')}
                      >
                        Update
                      </button>
                    </div>
                    <div style={{ color: '#bbb', fontWeight: 400, fontSize: 13, marginTop: 3, whiteSpace: 'nowrap' }}>
                      {productionViewMode === 'update' && pendingProductionSchedule
                        ? `${pendingTotalSongs} Songs | ${pendingRotationsCount} Rotations`
                        : `${totalLiveSongs} Songs | ${(liveProductionSchedules[selectedLiveSchedule]?.rotations || liveRotations).length} Rotations`}
                    </div>
                    <div style={{ color: '#bbb', fontWeight: 400, fontSize: 12, marginTop: 2 }}>
                      Updated {formatDate(lastLiveUpdate)}
                    </div>
                    {/* Show Go Live button below if in update mode and pending schedule exists */}
                    {productionViewMode === 'update' && pendingProductionSchedule && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            void handleCancelPending();
                          }}
                          style={{ fontSize: 10, padding: '2px 4px', height: 24 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleGoLiveConfirm}
                          disabled={!pendingProductionSchedule}
                          style={{ fontSize: 10, padding: '2px 4px', height: 24 }}
                        >
                          Go Live
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {productionViewMode === 'live' ? (
                // Render static list in Live mode (no DnD)
                <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', paddingRight: 12, paddingLeft: 0, paddingTop: 0, paddingBottom: 0, marginTop: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 80, marginRight: 16, marginLeft: 16 }}>
                    {(liveProductionSchedules[selectedLiveSchedule]?.rotations || []).map((rotation, idx) => (
                      <div key={rotation.id} style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontWeight: 700, color: '#23272b', fontSize: 13, marginBottom: 10 }}>
                        {rotation.labelName || `Rotation A${(idx + 1).toString().padStart(2, '0')}`}
                      </div>
                    ))}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {(liveProductionSchedules[selectedLiveSchedule]?.rotations || []).map((rotation, idx) => (
                      <div
                        key={rotation.id}
                        style={{
                          background: '#f7f7f7',
                          borderRadius: 7,
                          padding: '10px 14px',
                          marginBottom: 10,
                          fontSize: 13,
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          minHeight: 48,
                          cursor: 'pointer',
                          border: 'none',
                        }}
                        onClick={() => handleOpenRotationModal(rotation.id, 'production')}
                      >
                        <span style={{ flex: 1 }}>{rotation.name} <span style={{ color: '#888', fontWeight: 400 }}>({productionSongsByRotation[rotation.id]?.length || 0})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                pendingProductionSchedule ? (
                  <SortableContext items={pendingProductionSchedule.rotations.map(r => r.id)} strategy={verticalListSortingStrategy}>
                    <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', paddingRight: 12, paddingLeft: 0, paddingTop: 0, paddingBottom: 0, marginTop: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 80, marginRight: 16, marginLeft: 16 }}>
                        {pendingProductionSchedule.rotations.map((rotation, idx) => (
                          <div key={rotation.id} style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontWeight: 700, color: '#23272b', fontSize: 13, marginBottom: 10 }}>
                            {rotation.labelName || `Rotation A${(idx + 1).toString().padStart(2, '0')}`}
                          </div>
                        ))}
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {pendingProductionSchedule.rotations.map((rotation, idx) => (
                      <SortableRotation key={rotation.id} id={rotation.id}>
                            {(isDragging) => (
                              <DroppableRotation id={rotation.id} isDragging={isDragging}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <span style={{ flex: 1 }}>{rotation.name} <span style={{ color: '#888', fontWeight: 400 }}>({pendingSongsByRotation[rotation.id]?.length || 0})</span></span>
                        </div>
                              </DroppableRotation>
                            )}
                      </SortableRotation>
                    ))}
                      </div>
                  </div>
                </SortableContext>
                ) : null
              )}
            </Card>
          </Col>
        </Row>
        <DragOverlay>
          {draggedSong && (
            <div style={{ background: '#f7f7f7', borderRadius: 7, padding: '8px 12px', fontSize: 13, fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 180 }}>
              {draggedSong}
            </div>
          )}
        </DragOverlay>
      </DndContext>
      {/* Modal for viewing/editing songs in a rotation.
          Same chrome/top offset as the waveform player so stacked modals align. */}
      <Modal
        open={modalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        className="kk-modal-clear-card-headers"
        title={modalRotationId ? (
          <div style={{ fontWeight: 700, fontSize: 18, textAlign: 'center' }}>
            {rotationSetLabel(
              modalSource === 'staging'
                ? selectedStagingSchedule
                : selectedLiveSchedule,
            )}{' '}
            |{' '}
            {(
              stagingRotations.find(r => r.id === modalRotationId) ||
              productionRotations.find(r => r.id === modalRotationId) ||
              (pendingProductionSchedule?.rotations || []).find(r => r.id === modalRotationId)
            )?.name}{' '}
            <span style={{ fontWeight: 400, fontSize: 15, color: '#888' }}>
              ({modalSongObjects.length} songs)
            </span>
            {modalSource === 'staging' ? (
              <button
                type="button"
                onClick={() => {
                  const rot =
                    stagingRotations.find((r) => r.id === modalRotationId) ||
                    null;
                  if (rot) handleOpenRenameRotation(rot);
                }}
                style={{
                  marginLeft: 12,
                  border: 'none',
                  background: 'none',
                  color: '#e21e26',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Rename
              </button>
            ) : null}
          </div>
        ) : ''}
        styles={{ body: { padding: 0 } }}
        getContainer={() => document.body}
      >
        {/* Blue subheader */}
        <div style={{ background: '#23272b', color: 'white', padding: '12px 20px', display: 'flex', alignItems: 'center', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedSongs.length > 0 && !allSelected}
            onChange={e => handleSelectAll(e.target.checked)}
            style={{ marginRight: 12, accentColor: '#fff' }}
          />
          <span style={{ fontWeight: 600, fontSize: 15, marginRight: 12 }}>Move to Staging Rotation</span>
          <span style={{ margin: '0 10px', fontWeight: 400 }}>|</span>
          <Select
            showSearch
            placeholder="Select rotation"
            style={{ minWidth: 300, background: 'white', borderRadius: 6 }}
            options={moveOptions}
            value={moveTarget}
            onChange={handleMoveTargetChange}
            disabled={selectedSongs.length === 0}
            dropdownStyle={{ borderRadius: 8 }}
          />
          {/* Buttons right-aligned in subheader */}
          {selectedSongs.length > 0 && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button size="sm" className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => setSelectedSongs([])}>
                Cancel
              </Button>
              <Button size="sm" className={`${styles.btnPrimary} ${styles.btnSmall}`} onClick={handleMoveSongs} disabled={!moveTarget}>
                Move
              </Button>
            </div>
          )}
        </div>
        {/* Song list */}
        <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 0, padding: 24 }}>
          {modalSongObjects.map((song) => (
            <div key={song.rotationTrackId} style={{ display: 'flex', alignItems: 'center', marginBottom: 10, background: selectedSongs.includes(song.rotationTrackId) ? '#e6f7ff' : 'white', borderRadius: 6, padding: '6px 10px', transition: 'background 0.2s' }}>
              <Checkbox
                checked={selectedSongs.includes(song.rotationTrackId)}
                onChange={() => handleSongCheckbox(song.rotationTrackId)}
                style={{ marginRight: 12 }}
              />
              <span style={{ fontSize: 15, flex: 1 }}>{song.label}</span>
              <PlayCircleOutlined
                style={{ fontSize: 20, color: '#e21e26', marginLeft: 10, cursor: 'pointer', flexShrink: 0 }}
                title="Preview waveform"
                onClick={(e) => {
                  e.stopPropagation();
                  handleModalSongWaveformClick(song);
                }}
              />
              {modalSource === 'staging' ? (
                <CloseOutlined
                  style={{ color: '#888', fontSize: 16, cursor: 'pointer', marginLeft: 10 }}
                  onClick={() => handleRemoveSong(song.rotationTrackId)}
                />
              ) : null}
            </div>
          ))}
          {modalSongObjects.length === 0 && (
            <div style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>No songs in this rotation.</div>
          )}
        </div>
        {/* Move confirmation modal */}
        <Modal
          open={showMoveConfirm}
          onCancel={handleCancelMove}
          footer={null}
          width={400}
          centered
          style={{ left: 180, top: -80 }}
        >
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
              Are you sure you want to move {selectedSongs.length} song{selectedSongs.length > 1 ? 's' : ''} to <span style={{ color: '#1890ff' }}>{moveTargetLabel}</span>?
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
              <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelMove}>
                Cancel
              </Button>
              <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmMove}>
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
        {/* Delete confirmation modal */}
        <Modal
          open={deleteConfirmOpen}
          onCancel={handleCancelDeleteSong}
          onOk={handleConfirmDeleteSong}
          okText="Confirm"
          cancelText="Cancel"
          title={null}
          footer={null}
          width={400}
          style={{ left: 180, top: 200 }}
        >
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
              Are you sure you want to delete this song?
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
              <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelDeleteSong}>
                Cancel
              </Button>
              <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmDeleteSong}>
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      </Modal>
      {/* Duplicate song modal (not nested) */}
      <Modal
        open={duplicateModalOpen}
        onCancel={() => {
          setDuplicateModalOpen(false);
          setDuplicateSongNotice(null);
        }}
        footer={null}
        width={420}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            <div>{duplicateSongNotice?.headline}</div>
            {duplicateSongNotice?.locationLabel ? (
              <div style={{ marginTop: 14 }}>
                {duplicateSongNotice.locationRotationId ? (
                  <button
                    type="button"
                    onClick={() => {
                      const rotationId = duplicateSongNotice.locationRotationId;
                      if (!rotationId) return;
                      setDuplicateModalOpen(false);
                      setDuplicateSongNotice(null);
                      handleOpenRotationModal(rotationId, 'staging');
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      color: '#e21e26',
                      fontWeight: 700,
                      fontSize: 17,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Currently located in {duplicateSongNotice.locationLabel}
                  </button>
                ) : (
                  <span>
                    Currently located in {duplicateSongNotice.locationLabel}
                  </span>
                )}
              </div>
            ) : null}
          </div>
          <Button
            size="sm"
            style={{
              background: '#e21e26',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              borderRadius: 4,
              minWidth: 90,
            }}
            onClick={() => {
              setDuplicateModalOpen(false);
              setDuplicateSongNotice(null);
            }}
          >
            OK
          </Button>
        </div>
      </Modal>
      {/* Add rotation modal (two-step) — brand footer, not Ant Design blue OK */}
      <Modal
        open={addRotationModalOpen}
        onCancel={handleCancelAddRotationModal}
        title={addRotationStep === 1 ? 'What would you like to add?' : addRotationType === 'schedule' ? 'Add New Rotation Schedule' : 'Add New Rotation'}
        width={400}
        centered
        footer={null}
        styles={{ body: { padding: '20px 24px 24px' } }}
      >
        {addRotationStep === 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button
              type="button"
              style={{ padding: '14px 18px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 16, fontWeight: 600, background: '#f7f7f7', cursor: 'pointer', color: '#23272b' }}
              onClick={() => {
                setAddRotationType('schedule');
                setAddRotationStep(2);
                setScheduleFormError(null);
              }}
            >
              Add a new Rotation Schedule
            </button>
            <button
              type="button"
              style={{ padding: '14px 18px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 16, fontWeight: 600, background: '#f7f7f7', cursor: 'pointer', color: '#23272b' }}
              onClick={() => {
                setAddRotationType('rotation');
                setAddRotationStep(2);
                setScheduleFormError(null);
              }}
            >
              Add a new Rotation
            </button>
          </div>
        ) : (
          <>
            {addRotationType === 'schedule' ? (
              <div>
                <input
                  type="text"
                  value={newScheduleName}
                  onChange={(e) => {
                    setNewScheduleName(e.target.value);
                    if (scheduleFormError) setScheduleFormError(null);
                  }}
                  placeholder="Schedule key (e.g. D or Rotation D)"
                  maxLength={MAX_ROTATION_SET_LEN + 'Rotation '.length}
                  className={`${styles.modalTextInput}${scheduleFormError ? ` ${styles.modalTextInputError}` : ''}`}
                  autoFocus
                  aria-invalid={Boolean(scheduleFormError)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddRotation();
                    }
                  }}
                />
                {scheduleFormError ? (
                  <p
                    style={{
                      margin: '10px 0 0',
                      fontSize: 13,
                      color: '#e21e26',
                      fontWeight: 600,
                    }}
                    role="alert"
                  >
                    {scheduleFormError}
                  </p>
                ) : (
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: '#888' }}>
                    Schedule keys are short (max {MAX_ROTATION_SET_LEN} chars). Name
                    individual rotations anything you want (up to{' '}
                    {MAX_CATEGORY_LABEL_LEN} chars) and rename them anytime.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={newRotationName}
                  onChange={e => setNewRotationName(e.target.value)}
                  placeholder="Enter rotation name (e.g., Gold 2000-2003 EDM)"
                  maxLength={MAX_CATEGORY_LABEL_LEN}
                  className={styles.modalTextInput}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddRotation();
                    }
                  }}
                />
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 24,
              }}
            >
              <Button
                size="sm"
                variant="secondary"
                style={{
                  background: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  fontWeight: 600,
                  borderRadius: 4,
                  minWidth: 90,
                }}
                onClick={handleCancelAddRotationModal}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                style={{
                  background: '#e21e26',
                  color: 'white',
                  border: 'none',
                  fontWeight: 600,
                  borderRadius: 4,
                  minWidth: 90,
                }}
                onClick={() => {
                  void handleAddRotation();
                }}
                disabled={
                  addRotationType === 'schedule'
                    ? !newScheduleName.trim()
                    : !newRotationName.trim()
                }
              >
                Add
              </Button>
            </div>
          </>
        )}
      </Modal>
      {/* Rename rotation (categoryLabel) — Staging only */}
      <Modal
        open={renameRotationOpen}
        onCancel={handleCancelRenameRotation}
        title="Rename Rotation"
        width={400}
        centered
        footer={null}
        styles={{ body: { padding: '20px 24px 24px' } }}
      >
        <div>
          <input
            type="text"
            value={renameRotationDraft}
            onChange={(e) => setRenameRotationDraft(e.target.value)}
            placeholder="Rotation name"
            maxLength={MAX_CATEGORY_LABEL_LEN}
            disabled={renameRotationBusy}
            className={styles.modalTextInput}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleConfirmRenameRotation();
              }
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 24,
          }}
        >
          <Button
            size="sm"
            variant="secondary"
            style={{
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              fontWeight: 600,
              borderRadius: 4,
              minWidth: 90,
            }}
            onClick={handleCancelRenameRotation}
            disabled={renameRotationBusy}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            style={{
              background: '#e21e26',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              borderRadius: 4,
              minWidth: 90,
            }}
            onClick={() => {
              void handleConfirmRenameRotation();
            }}
            disabled={renameRotationBusy || !renameRotationDraft.trim()}
          >
            Save
          </Button>
        </div>
      </Modal>
      {/* Delete rotation confirmation modal */}
      <Modal
        open={deleteRotationConfirmOpen}
        onCancel={handleCancelDeleteRotation}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Are you sure you want to delete this rotation?
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
            <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelDeleteRotation}>
              Cancel
            </Button>
            <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmDeleteRotation}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      {/* Delete live rotation confirmation modal */}
      <Modal
        open={deleteLiveRotationConfirmOpen}
        onCancel={handleCancelDeleteLiveRotation}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Are you sure you want to delete this rotation?
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
            <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelDeleteLiveRotation}>
              Cancel
            </Button>
            <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmDeleteLiveRotation}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      {/* Go Live modal */}
      <Modal
        open={goLiveModalOpen}
        onCancel={() => setGoLiveModalOpen(false)}
        footer={null}
        width={420}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Are you sure you want to make this{' '}
            {rotationSetLabel(selectedLiveSchedule)} Schedule live?<br />
            This will make <span style={{ color: '#1890ff' }}>{displayChannel.name}</span> start using this{' '}
            {rotationSetLabel(selectedLiveSchedule)} Schedule.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
            <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={() => setGoLiveModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleGoLiveConfirm}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
      {/* Delete schedule confirmation modal */}
      <Modal
        open={deleteScheduleModalOpen}
        onCancel={handleCancelDeleteSchedule}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Are you sure you want to delete the rotation schedule <span style={{ color: '#e21e26' }}>{scheduleToDelete}</span>?
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
            <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelDeleteSchedule}>
              Cancel
            </Button>
            <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmDeleteSchedule}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      {/* Delete live schedule confirmation modal */}
      <Modal
        open={deleteLiveScheduleModalOpen}
        onCancel={handleCancelDeleteLiveSchedule}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Are you sure you want to delete the rotation schedule <span style={{ color: '#e21e26' }}>{liveScheduleToDelete}</span>?
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 18 }}>
            <Button size="sm" style={{ background: '#e0e0e0', color: '#333', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleCancelDeleteLiveSchedule}>
              Cancel
            </Button>
            <Button size="sm" style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600, borderRadius: 4, minWidth: 90 }} onClick={handleConfirmDeleteLiveSchedule}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      {/* Modal to display songs in a rotation */}
      <Modal
        title={`Songs in ${selectedRotation}`}
        open={rotationModalOpen}
        onCancel={() => setRotationModalOpen(false)}
        footer={null}
      >
        {selectedRotation && (stagingSchedules[selectedRotation] || []).map((rotation, index) => (
          <div key={index} style={{ marginBottom: 8 }}>
            <h4>{rotation.name}</h4>
            <ul>
              {(stagingSongsByRotation[rotation.id] || []).map((song, songIndex) => (
                <li key={songIndex}>{song.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </Modal>
      {/* Waveform Modal — same width/top as the rotation songs modal so they stack aligned. */}
      <Modal
        open={waveformModalOpen}
        onCancel={handleCloseWaveformModal}
        footer={null}
        width={800}
        // Positioned by the global Super Admin modal standard
        // (src/styles/modal-standard.scss) — below the fixed navbar +
        // environment banner. The extra `kk-modal-clear-card-headers`
        // modifier drops it below the page title and the dark
        // Staging/Production Rotation Schedules card headers too (owner QA
        // 2026-07-21: the player should not sit on top of the card headers).
        // Never reintroduce inline fixed positioning here — that was the
        // original under-the-navbar bug.
        className={
          modalOpen
            ? 'kk-modal-clear-card-headers kk-modal-reveal-peer-header'
            : 'kk-modal-clear-card-headers'
        }
        title={waveformSong ? waveformSong.name : ''}
        styles={{ body: { padding: 12, textAlign: 'center' } }}
        getContainer={() => document.body}
      >
        {audioUrl ? (
          <>
            <div style={{ height: 120, overflow: 'hidden', width: '100%' }}>
              <WaveSurfer
                height={120}
                waveColor="#e21e26"
                progressColor="#23272b"
                url={audioUrl}
                onReady={ws => setWavesurfer(ws)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 0, fontSize: 15, color: '#888', fontWeight: 500, letterSpacing: 1 }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            <div style={{ marginTop: 0, display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Button onClick={handlePlayPause} style={{ minWidth: 70, height: 28, lineHeight: '24px', fontWeight: 600, fontSize: 15, padding: '0 10px' }}>
                {isPlaying ? 'Stop' : 'Play'}
              </Button>
            </div>
          </>
        ) : (
          <div style={{ color: '#888', fontSize: 16, marginTop: 32 }}>No audio file found for this song.</div>
        )}
      </Modal>
      {/* Column stretching styles moved to SCSS module */}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(ChannelProgramming);